import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import DEACTIVATE_LICENSE from '../../../queries/changeLicenseQueries/deactivateLicenseMutation';
import ApolloError from '@shared/components/ApolloError';
import { showSuccessToast, showErrorToast } from '@shared/services/toastService';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import { DialogActions, DialogContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { DeactivateLicenseValidator } from './validators/deactivateLicenseValidator';
import { LinkToast } from '../../../../notifications/toastNotification/components/LinkToast';
import { pageUrlService } from '@shared/services/pageUrlService';

const FORM_ID = 'change-license';

const METADATA_EVENT_ID_KEY = 'EventId';

const DEACTIVATE_LICENSE_MESSAGE = 'User has been Deactivated.';
const DEACTIVATE_CONFIRMATION_MESSAGE = 'Are you sure you want to deactivate this licensed user?';
const DEACTIVATE_TEXT = 'Deactivate';
const CANCEL_TEXT = 'Cancel';
const SAVE_TEXT = 'Save';

/**
 * @typedef {object} DeactivateLicenseProps
 * @property {React.ReactNode} [dialogContent]
 * @property {React.ReactNode} [dialogActions]
 * @property {number} [externalUserId]
 * @property {string} [previousLink]
 */

/**
 * @param {DeactivateLicenseProps} props
 */
const DeactivateLicense = props => {
  const { dialogContent, dialogActions, externalUserId, previousLink } = props;

  const navigate = useNavigate();
  const client = useApolloClient();

  const form = useForm({
    resolver: joiResolver(DeactivateLicenseValidator.schema),
    defaultValues: {
      externalUserId
    }
  });
  const { handleSubmit } = form;

  const [deactivateLicense, { error: deactivateLicenseError }] = useMutation(DEACTIVATE_LICENSE, {
    update: () => client.clearStore()
  });

  const handleConfirm = async formData => {
    const result = await deactivateLicense({
      variables: { externalUserId: formData.externalUserId }
    });
    if (!result.data.deactivateUserLicense.Success) {
      result.data.deactivateUserLicense.Errors.forEach(err => {
        showErrorToast(
          <LinkToast
            message={err.Message}
            url={pageUrlService.getEditEventPageUrl(
              err.Metadata.find(x => x.Key === METADATA_EVENT_ID_KEY).Value
            )}
            openInNewTab={true}
          />
        );
      });

      return;
    }

    showSuccessToast(DEACTIVATE_LICENSE_MESSAGE);
    navigate(previousLink);
  };

  if (deactivateLicenseError) {
    return <ApolloError />;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(handleConfirm)}>
      <DialogContent>{dialogContent}</DialogContent>

      <DialogActions>
        {dialogActions}

        <ConfirmDialogWithButton
          openModalBtnText={SAVE_TEXT}
          confirmBtnText={DEACTIVATE_TEXT}
          cancelBtnText={CANCEL_TEXT}
          message={DEACTIVATE_CONFIRMATION_MESSAGE}
          dialogMode={DIALOG_MODES.EXTERNAL_FORM_SUBMIT}
          formId={FORM_ID}
        />
      </DialogActions>
    </form>
  );
};

export default DeactivateLicense;
